import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { subscribeToStockNotification } from '../utils/stockNotifications';

interface StockNotificationFormProps {
  productId: string;
  productName: string;
  variant?: 'card' | 'detail';
}

const StockNotificationForm: React.FC<StockNotificationFormProps> = ({ 
  productId, 
  productName,
  variant = 'card' 
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    try {
      await subscribeToStockNotification(email, productId);
      setIsSuccess(true);
      setEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (variant === 'card') {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600 mb-3 font-medium">
          Get notified when this item is back in stock
        </p>
        {isSuccess ? (
          <div className="flex items-center text-green-600 text-sm">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            <span>You&apos;ll be notified when it&apos;s back!</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2b603e]/20"
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting || !email}
                className="bg-[#2b603e] hover:bg-[#1a3d25] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                    Notify
                  </>
                )}
              </button>
            </div>
            {error && (
              <p className="text-red-600 text-xs">{error}</p>
            )}
          </form>
        )}
      </div>
    );
  }

  // Detail page variant
  return (
    <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
      {isSuccess ? (
        <div className="flex items-center text-green-600">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          <span>You&apos;ll be notified when it&apos;s back in stock!</span>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-700 mb-3">
            Get notified when this product is back in stock
          </p>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2b603e]/20"
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting || !email}
                className="bg-[#2b603e] hover:bg-[#1a3d25] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  'Notify Me'
                )}
              </button>
            </div>
            {error && (
              <p className="text-red-600 text-xs">{error}</p>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default StockNotificationForm;

